import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CATEGORY_ICONS } from "../../../data/demoNetworkData";
import InsightDetailModal from "./InsightDetailModal";

export default function NetworkInsightsPanel({ insights }) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const CARD_WIDTH = 220;
  const GAP = 12;
  
  const handleCardClick = (insight) => {
    setSelectedInsight(insight);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedInsight(null), 200);
  };
  
  const checkScrollability = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
    }
  };
  
  useEffect(() => {
    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => window.removeEventListener('resize', checkScrollability);
  }, []);
  
  const scrollBy = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = (CARD_WIDTH + GAP) * direction;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkScrollability, 300);
    }
  };
  
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return '↑';
      case 'down': return '↓';
      default: return '→';
    }
  };
  
  return (
    <div className="network-insights-panel">
      {canScrollLeft && (
        <button 
          className="carousel-arrow carousel-arrow-left"
          onClick={() => scrollBy(-1)}
          aria-label="Scroll left"
        >
          ‹
        </button>
      )}
      
      <div 
        className="insights-scroll" 
        ref={scrollRef}
        onScroll={checkScrollability}
      >
        {insights.map((insight) => (
          <motion.div
            key={insight.id}
            className="insight-card clickable"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => handleCardClick(insight)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="insight-header">
              <span className="insight-icon">{CATEGORY_ICONS[insight.type]}</span>
              <span className="insight-title">{insight.title}</span>
              {insight.aiGenerated && (
                <span className="ai-badge">AI</span>
              )}
            </div>
            <p className="insight-description">{insight.description}</p>
            <div className={`insight-trend ${insight.trend}`}>
              <span>{getTrendIcon(insight.trend)}</span>
              <span>{insight.trend === 'up' ? t('network.insights.improving', 'Improving') : 
                     insight.trend === 'down' ? t('network.insights.declining', 'Declining') :
                     t('network.insights.stable', 'Stable')}</span>
            </div>
          </motion.div>
        ))}
      </div>
      
      {canScrollRight && (
        <button 
          className="carousel-arrow carousel-arrow-right"
          onClick={() => scrollBy(1)}
          aria-label="Scroll right"
        >
          ›
        </button>
      )}
      
      <InsightDetailModal 
        insight={selectedInsight}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
